import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  AlertTitle,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AppDispatch, RootState } from '../store/store';
import { fetchClaimById, updateClaim } from '../services/claimsApi';
import { Claim, LossType } from '../store/claimsSlice';
import { getInventoryItems, getLossTypes, InventoryItem } from '../services/claimsApi';
import { claimColors, claimSx } from '../styles/claimPageStyles';
import { formatDateOnlyIso, parseDateOnlyLocal } from '../utils/dateOnly';

const formatCurrencyForDisplay = (value: number | string | null | undefined): string => {
  if (value == null || value === '') return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : Number(value);
  return isNaN(num) ? '' : num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const parseCurrencyForApi = (value: number | string | null | undefined): number => {
  if (value == null || value === '') return 0;
  const str = typeof value === 'number' ? String(value) : String(value).replace(/,/g, '');
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
};

const ClaimEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentClaim: claim, loading, error, fieldErrors } = useSelector((state: RootState) => state.claims);
  const { register, handleSubmit, reset, control, watch, setValue, formState: { errors } } = useForm<Partial<Claim>>();
  const [lossTypes, setLossTypes] = useState<LossType[]>([]);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);

  const adjusterLossTypeId = watch('adjuster_loss_type.id');
  const statusValue = watch('status');
  const otherLossType = lossTypes.find((t) => t.name === 'Other');
  const isAdjusterOther = otherLossType && adjusterLossTypeId === otherLossType.id;

  // Map status values to display labels
  const statusLabels: Record<string, string> = {
    'SUBMITTED': 'Submitted',
    'IN_REVIEW': 'In Review',
    'PENDING_REVIEW': 'Pending Review',
    'INCOMPLETE': 'Incomplete',
    'APPROVED': 'Approved',
    'DENIED': 'Denied',
    'DRAFT': 'Draft',
    'LEGAL_MEDIATION': 'Legal Mediation',
    'SITE_CLOSED': 'Site Closed',
    'WITH_OPERATIONS': 'With Operations',
    'PENDING_SIGNATURE': 'Pending Signature',
    'PAID': 'Paid',
    'CLOSED': 'Closed',
    'DUPLICATE': 'Duplicate'
  };

  useEffect(() => {
    const fetchLossTypes = async () => {
      try {
        const types = await getLossTypes();
        setLossTypes(types);
      } catch (error) {
        console.error('Error fetching loss types:', error);
      }
    };
    fetchLossTypes();
  }, []);

  useEffect(() => {
    if (id) {
      dispatch(fetchClaimById(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (!id) return;
    const loadInventory = async () => {
      try {
        const items = await getInventoryItems(id);
        setInventoryItems(Array.isArray(items) ? items : []);
      } catch (inventoryError) {
        console.error('Error loading inventory items:', inventoryError);
        setInventoryItems([]);
      }
    };
    loadInventory();
  }, [id]);

  useEffect(() => {
    if (claim) {
      const formattedClaim = {
        ...claim,
        resolved_date: parseDateOnlyLocal(claim.resolved_date),
        // Convert boolean/true/false values to 1/0 for Yes/No radio groups
        is_account_current: claim.is_account_current ? 1 : 0,
        is_police_report_attached: claim.is_police_report_attached ? 1 : 0,
        is_media_submitted: claim.is_media_submitted ? 1 : 0,
        is_authorities_involved: claim.is_authorities_involved ? 1 : 0,
        is_tpp_in_place: claim.is_tpp_in_place ? 1 : 0,
        total_paid: formatCurrencyForDisplay(claim.total_paid),
        tpp_amount: formatCurrencyForDisplay(claim.tpp_amount),
      } as any;
      reset(formattedClaim);
    }
  }, [claim, reset]);

  const onSubmit = async (data: Partial<Claim>) => {
    try {
      if (id) {
        const d = data as any;

        // Convert resolved_date from Date object to YYYY-MM-DD string if it exists
        let resolvedDate: string | null = null;
        const resolvedDateVal = d.resolved_date;
        if (resolvedDateVal && resolvedDateVal instanceof Date) {
          resolvedDate = formatDateOnlyIso(resolvedDateVal);
        }

        const toYesNo = (value: unknown): number => {
          if (value === 1 || value === true || value === "true" || value === "True" || value === "Yes" || value === "yes") return 1;
          return 0;
        };

        const adjusterId = d.adjuster_loss_type?.id ?? d['adjuster_loss_type.id'];
        const effectiveOtherLossType = (otherLossType && adjusterId === otherLossType.id) ? (d.other_loss_type ?? null) : null;

        // Build payload with ONLY edit form fields
        const payload: Record<string, unknown> = {
          id,
          status: d.status,
          resolved_date: resolvedDate,
          total_paid: parseCurrencyForApi(d.total_paid),
          tpp_amount: parseCurrencyForApi(d.tpp_amount),
          is_account_current: toYesNo(d.is_account_current),
          is_police_report_attached: toYesNo(d.is_police_report_attached),
          is_media_submitted: toYesNo(d.is_media_submitted),
          is_authorities_involved: toYesNo(d.is_authorities_involved),
          is_tpp_in_place: toYesNo(d.is_tpp_in_place),
          adjuster_tpp_loss_type_id: (adjusterId !== undefined && adjusterId !== null && adjusterId !== '') ? adjusterId : null,
          other_loss_type: effectiveOtherLossType,
          note: d.note ?? null,
          deny_reason: d.deny_reason ?? null,
        };

        await dispatch(updateClaim(payload as Partial<Claim> & { id: string })).unwrap();
        const queryString = searchParams.toString();
        navigate(`/claims/${id}${queryString ? `?${queryString}` : ''}`, {
          state: { claimUpdated: true },
        });
      }
    } catch (error) {
      console.error('Error updating claim:', error);
    }
  };

  const formatMoney = (value: number | string | null | undefined): string => {
    const num = Number(value ?? 0);
    if (Number.isNaN(num)) return '0.00';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const totalPrice = useMemo(() => {
    const sumInventory = () =>
      inventoryItems.reduce((sum, item) => sum + (Number(item.price) || 0), 0);

    const raw = claim?.damage_estimate;
    if (raw === null || raw === undefined) return sumInventory();

    if (typeof raw === 'string') {
      const trimmed = raw.replace(/,/g, '').trim();
      if (trimmed === '') return sumInventory();
      const n = Number(trimmed);
      return Number.isNaN(n) ? sumInventory() : n;
    }

    const n = Number(raw);
    return Number.isNaN(n) ? sumInventory() : n;
  }, [claim?.damage_estimate, inventoryItems]);

  if (loading) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  if (error && !claim) {
    return (
      <Container>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  if (!claim) {
    return (
      <Container>
        <Typography>Claim not found</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6" sx={{ color: claimColors.navy, fontWeight: 600 }}>
            <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>Claim ID:</Box>{' '}
            {claim.claim_number}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                const queryString = searchParams.toString();
                navigate(`/claims/${id}${queryString ? `?${queryString}` : ''}`);
              }}
              sx={claimSx.btnOutlined}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              form="claim-edit-form"
              sx={claimSx.btnPrimary}
            >
              Save Changes
            </Button>
          </Box>
        </Box>

        <Paper sx={{ ...claimSx.mainPaper, p: 3 }}>
          {(error || fieldErrors) && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <AlertTitle>{error || 'Unable to save changes'}</AlertTitle>
              {fieldErrors && (
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {Object.values(fieldErrors)
                    .flat()
                    .map((message, idx) => (
                      <li key={idx}>{message}</li>
                    ))}
                </Box>
              )}
            </Alert>
          )}
          <Box component="form" id="claim-edit-form" onSubmit={handleSubmit(onSubmit)}>
            <Box
              sx={{
                mb: 3,
                px: 2,
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: claimColors.cardMuted,
                borderRadius: 1,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 4,
              }}
            >
              <Typography sx={{ color: 'text.primary' }}>
                <Box component="span" sx={claimSx.label}>Name:</Box> {claim.reporter_first_name} {claim.reporter_last_name}
              </Typography>
              <Typography sx={{ color: 'text.primary' }}>
                <Box component="span" sx={claimSx.label}>Total Price:</Box> ${formatMoney(totalPrice)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  defaultValue={claim.status}
                  {...register('status', { required: 'Status is required' })}
                  error={!!errors.status}
                  helperText={errors.status?.message}
                >
                  <MenuItem value="DRAFT">{statusLabels['DRAFT']}</MenuItem>
                  <MenuItem value="SUBMITTED">{statusLabels['SUBMITTED']}</MenuItem>
                  <MenuItem value="IN_REVIEW">{statusLabels['IN_REVIEW']}</MenuItem>
                  <MenuItem value="PENDING_REVIEW">{statusLabels['PENDING_REVIEW']}</MenuItem>
                  <MenuItem value="INCOMPLETE">{statusLabels['INCOMPLETE']}</MenuItem>
                  <MenuItem value="APPROVED">{statusLabels['APPROVED']}</MenuItem>
                  <MenuItem value="DENIED">{statusLabels['DENIED']}</MenuItem>
                  <MenuItem value="LEGAL_MEDIATION">{statusLabels['LEGAL_MEDIATION']}</MenuItem>
                  <MenuItem value="SITE_CLOSED">{statusLabels['SITE_CLOSED']}</MenuItem>
                  <MenuItem value="WITH_OPERATIONS">{statusLabels['WITH_OPERATIONS']}</MenuItem>
                  <MenuItem value="PENDING_SIGNATURE">{statusLabels['PENDING_SIGNATURE']}</MenuItem>
                  <MenuItem value="PAID">{statusLabels['PAID']}</MenuItem>
                  <MenuItem value="CLOSED">{statusLabels['CLOSED']}</MenuItem>
                  <MenuItem value="DUPLICATE">{statusLabels['DUPLICATE']}</MenuItem>
                </TextField>
              </Box>

              <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Controller
                    name="resolved_date"
                    control={control}
                    rules={{
                      validate: (value) => {
                        if (statusValue !== 'APPROVED') return true;
                        const v = value as Date | string | null | undefined;
                        if (!v) {
                          return 'Resolved Date is required when status is Approved';
                        }
                        if (v instanceof Date && isNaN(v.getTime())) {
                          return 'Resolved Date is required when status is Approved';
                        }
                        return true;
                      },
                    }}
                    render={({ field, fieldState }) => {
                      // Convert string to Date if needed, or use Date object directly
                      let dateValue: Date | null = null;
                      const fieldValue = field.value as Date | string | null | undefined;
                      if (fieldValue) {
                        dateValue = parseDateOnlyLocal(fieldValue);
                      }
                      return (
                        <DatePicker
                          label="Resolved Date"
                          value={dateValue}
                          onChange={(newValue) => field.onChange(newValue)}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!fieldState.error,
                              helperText: fieldState.error?.message,
                            },
                          }}
                        />
                      );
                    }}
                  />
                </LocalizationProvider>
              </Box>

              <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                <Controller
                  name="total_paid"
                  control={control}
                  rules={{
                    validate: (value) => {
                      const num = parseCurrencyForApi(value);
                      if (num < 0) {
                        return "Total Paid can't be negative";
                      }
                      if (statusValue === 'APPROVED' && num <= 0) {
                        return 'Total Paid must be greater than zero when status is Approved';
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="text"
                      label="Total Paid"
                      error={!!errors.total_paid}
                      helperText={errors.total_paid?.message}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^\d,.]/g, '');
                        const parts = v.split('.');
                        const formatted = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : parts.length === 2 && parts[1].length > 2 ? parts[0] + '.' + parts[1].slice(0, 2) : v;
                        field.onChange(formatted);
                      }}
                      InputProps={{
                        startAdornment: <span>$</span>,
                      }}
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  )}
                />
              </Box>

            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, my: 7 }}>
              <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                <FormControl component="fieldset">
                  <FormLabel>Account Current</FormLabel>
                  <Controller
                    name="is_account_current"
                    control={control}
                    defaultValue={(claim.is_account_current ? 1 : 0) as any}
                    render={({ field }) => (
                      <RadioGroup
                        row
                        {...field}
                        value={field.value ?? 0}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      >
                        <FormControlLabel value={1} control={<Radio />} label="Yes" />
                        <FormControlLabel value={0} control={<Radio />} label="No" />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Box>

              <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                <FormControl component="fieldset">
                  <FormLabel>Police Report Attached</FormLabel>
                  <Controller
                    name="is_police_report_attached"
                    control={control}
                    defaultValue={(claim.is_police_report_attached ? 1 : 0) as any}
                    render={({ field }) => (
                      <RadioGroup
                        row
                        {...field}
                        value={field.value ?? 0}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      >
                        <FormControlLabel value={1} control={<Radio />} label="Yes" />
                        <FormControlLabel value={0} control={<Radio />} label="No" />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Box>

              <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                <FormControl component="fieldset">
                  <FormLabel>Media Submitted</FormLabel>
                  <Controller
                    name="is_media_submitted"
                    control={control}
                    defaultValue={(claim.is_media_submitted ? 1 : 0) as any}
                    render={({ field }) => (
                      <RadioGroup
                        row
                        {...field}
                        value={field.value ?? 0}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      >
                        <FormControlLabel value={1} control={<Radio />} label="Yes" />
                        <FormControlLabel value={0} control={<Radio />} label="No" />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Box>

              <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                <FormControl component="fieldset">
                  <FormLabel>Authorities Involved</FormLabel>
                  <Controller
                    name="is_authorities_involved"
                    control={control}
                    defaultValue={(claim.is_authorities_involved ? 1 : 0) as any}
                    render={({ field }) => (
                      <RadioGroup
                        row
                        {...field}
                        value={field.value ?? 0}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      >
                        <FormControlLabel value={1} control={<Radio />} label="Yes" />
                        <FormControlLabel value={0} control={<Radio />} label="No" />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Box>

              <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                <FormControl component="fieldset">
                  <FormLabel>TPP in Place</FormLabel>
                  <Controller
                    name="is_tpp_in_place"
                    control={control}
                    defaultValue={(claim.is_tpp_in_place ? 1 : 0) as any}
                    render={({ field }) => (
                      <RadioGroup
                        row
                        {...field}
                        value={field.value ?? 0}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      >
                        <FormControlLabel value={1} control={<Radio />} label="Yes" />
                        <FormControlLabel value={0} control={<Radio />} label="No" />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, my: 7 }}>
              <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                <Controller
                  name="tpp_amount"
                  control={control}
                  rules={{
                    validate: (value) => {
                      const num = parseCurrencyForApi(value);
                      if (num < 0) {
                        return "TPP Amount can't be neagtive";
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="text"
                      label="TPP Amount"
                      error={!!errors.tpp_amount}
                      helperText={errors.tpp_amount?.message}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^\d,.]/g, '');
                        const parts = v.split('.');
                        const formatted = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : parts.length === 2 && parts[1].length > 2 ? parts[0] + '.' + parts[1].slice(0, 2) : v;
                        field.onChange(formatted);
                      }}
                      InputProps={{
                        startAdornment: <span>$</span>,
                      }}
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  )}
                />
              </Box>

              <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                <Controller
                  name="adjuster_loss_type.id"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (statusValue === 'APPROVED') {
                        if (value === undefined || value === null || value === '') {
                          return 'Adjuster Reason is required when status is Approved';
                        }
                      }
                      return true;
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      select
                      label="Adjuster Reason For Claim"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      onChange={(e) => {
                        field.onChange(e);
                        if (otherLossType && e.target.value !== otherLossType.id) {
                          setValue('other_loss_type', null);
                        }
                      }}
                    >
                      {lossTypes
                        .filter((type) => type.name !== 'Building Collapse')
                        .map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.name}
                          </MenuItem>
                        ))}
                    </TextField>
                  )}
                />
              </Box>

              {isAdjusterOther && (
                <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                  <TextField
                    fullWidth
                    label="Other Loss Type"
                    {...register('other_loss_type', {
                      required: isAdjusterOther ? 'Other loss type is required when Adjuster Reason is Other' : false,
                      validate: (value) => {
                        if (!isAdjusterOther) return true;
                        return (value && String(value).trim() !== '') ? true : 'Other loss type is required when Adjuster Reason is Other';
                      },
                    })}
                    error={!!errors.other_loss_type}
                    helperText={errors.other_loss_type?.message}
                  />
                </Box>
              )}

              <Box sx={{ flex: '1 1 100%', minWidth: 0 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Progress Notes"
                  {...register('note', { required: 'Description is required' })}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                />
              </Box>

              <Box sx={{ flex: '1 1 100%', minWidth: 0 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Deny Reason"
                  {...register('deny_reason', {
                    validate: (value) => {
                      if (statusValue === 'DENIED') {
                        if (!value || String(value).trim() === '') {
                          return 'Deny Reason is required when status is Denied';
                        }
                      } else if (statusValue === 'APPROVED') {
                        if (value && String(value).trim() !== '') {
                          return 'Deny Reason must be empty when status is Approved';
                        }
                      }
                      return true;
                    },
                  })}
                  error={!!errors.deny_reason}
                  helperText={errors.deny_reason?.message}
                />
              </Box>

            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ClaimEdit; 